import React from "react";
import { AlertTriangle, Building2, RefreshCw } from "lucide-react";
import ServiceTitanCredentials from "../ServiceTitanCredentials/ServiceTitanCredentials";
import ServiceTitanBusinessUnitMapping from "../ServiceTitanBusinessUnitMapping/ServiceTitanBusinessUnitMapping";
import { useServiceTitanIntegration } from "@/modules/hub/features/CompanyManagement/hooks/useServiceTitanIntegration";

interface ServiceTitanIntegrationProps {
  companyUuid?: string;
  companyId?: number;
  isEditMode?: boolean;
}

const ServiceTitanIntegration: React.FC<ServiceTitanIntegrationProps> = ({
  companyUuid,
}) => {
  const state = useServiceTitanIntegration(companyUuid);
  const { businessUnits, credentialError, credentialLoading } = state;

  if (credentialLoading) {
    return (
      <div className="space-y-6 min-h-[600px] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="h-6 w-6 text-gray-400 dark:text-slate-500 animate-spin" />
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Loading ServiceTitan credentials...
          </p>
        </div>
      </div>
    );
  }

  if (credentialError) {
    return (
      <div className="space-y-6 min-h-[600px]">
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium text-red-800 dark:text-red-300">
                Connection Error:{" "}
              </span>
              <span className="text-red-700 dark:text-red-400">
                {credentialError}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-h-[600px]">
      {state.credentialsSaveError && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium text-red-800 dark:text-red-300">
                Credential Error:{" "}
              </span>
              <span className="text-red-700 dark:text-red-400">
                {state.credentialsSaveError}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
        <ServiceTitanCredentials
          heading={
            <div className="flex items-center">
              <Building2 className="h-5 w-5 mr-2 text-gray-600 dark:text-slate-400" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Connected Account
              </h3>
            </div>
          }
          state={state}
        />
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <Building2 className="h-5 w-5 mr-2 text-gray-600 dark:text-slate-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Business Units Found: {businessUnits.length}
            </h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Map each business unit to a trade category
          </p>
        </div>

        <ServiceTitanBusinessUnitMapping state={state} />
      </div>
    </div>
  );
};

export default ServiceTitanIntegration;
